import { call } from '@/lib/api';
import { MondayItem } from '@/types';
import {
  ActionIcon,
  Combobox,
  Highlight,
  Paper,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { useDebouncedValue, useHover } from '@mantine/hooks';
import { JSX, useCallback, useEffect, useState } from 'react';
import { IoIosCloseCircle, IoIosCloseCircleOutline } from 'react-icons/io';
import { useLocation, useParams } from 'wouter';
import Loader from '../Loader';

export type TItemRenderer = (props: TItemRendererProps) => JSX.Element;

export type TItemRendererProps = {
  item: MondayItem;
  term: string;
};

function Clear({ onClick }: { onClick: () => void }) {
  const { hovered, ref } = useHover();
  return (
    <div ref={ref}>
      <ActionIcon onClick={onClick} variant="transparent">
        {hovered ? <IoIosCloseCircle /> : <IoIosCloseCircleOutline />}
      </ActionIcon>
    </div>
  );
}

function Option({
  item,
  term,
  onClick,
  ItemRenderer,
}: {
  item: MondayItem;
  term: string;
  onClick: () => void;
  ItemRenderer: TItemRenderer;
}) {
  return (
    <Combobox.Option key={item.id} value={item.id} onClick={onClick}>
      <ItemRenderer item={item} term={term} />
    </Combobox.Option>
  );
}

export default function Search({
  placeholder,
  endpoint,
  onRecentsLoaded,
  onSelect,
  ItemRenderer = ({ item, term }) => (
    <Highlight highlight={term}>{item.name}</Highlight>
  ),
}: {
  placeholder: string;
  endpoint: 'contact/search' | 'company/search';
  ItemRenderer?: TItemRenderer;
  onRecentsLoaded?: (recents: MondayItem[]) => void;
  onSelect?: (item: MondayItem) => void;
}) {
  const getRecents = useCallback(
    (excludeId?: string): MondayItem[] => {
      let recents: MondayItem[] = JSON.parse(
        localStorage.getItem(`search:recent:${endpoint}`) || '[]',
      );
      if (recents.length > 1) {
        recents = recents.filter((r) => r.id != excludeId);
      }
      return recents;
    },
    [endpoint],
  );

  const addRecent = useCallback(
    (contact: MondayItem) => {
      let recents = getRecents();
      recents.unshift(contact);
      recents = recents
        .filter((item, i, a) => a.findIndex((ai) => ai.id === item.id) === i)
        .slice(0, 10);
      localStorage.setItem(
        `search:recent:${endpoint}`,
        JSON.stringify(recents),
      );
    },
    [endpoint, getRecents],
  );

  const combo = useCombobox({});
  const [_, nav] = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [termDebounced] = useDebouncedValue(term, 1000);
  const [results, setResults] = useState<MondayItem[]>([]);
  const [recents, setRecents] = useState<MondayItem[]>(getRecents(id));
  const [prefiled, setPrefiiled] = useState('');

  onSelect = onSelect || ((item) => nav(`./${item.id}`));

  useEffect(() => {
    const recents = getRecents(id);
    setRecents(recents);
    if (onRecentsLoaded) {
      onRecentsLoaded(recents);
    }
  }, [id, onRecentsLoaded, getRecents]);

  useEffect(() => {
    const signal = new AbortController();
    if (termDebounced?.length >= 3) {
      setLoading(true);
      call<MondayItem[]>(endpoint, { json: { term: termDebounced } })
        .then((res) => res.json())
        .then(setResults)
        .finally(() => setLoading(false));
    }
    return () => {
      signal.abort();
    };
  }, [termDebounced, endpoint]);

  useEffect(() => {
    if (!term) {
      setResults([]);
      setRecents(getRecents(id));
    } else if (term.length < 3) {
      setResults([]);
      setRecents([]);
    } else {
      setLoading(true);
    }
  }, [term, getRecents, id]);

  return (
    <Paper
      maw="960px"
      bg="white"
      pos="relative"
      m={'md'}
      style={{ zIndex: '+1' }}
    >
      <Combobox store={combo} position="bottom" offset={1} radius={0}>
        <Combobox.Target>
          <TextInput
            variant="filled"
            autoComplete="off"
            name="search-no-autofill"
            rightSection={
              (term || prefiled) && (
                <Clear
                  onClick={() => {
                    setTerm('');
                    setPrefiiled('');
                  }}
                />
              )
            }
            onClick={() => combo.openDropdown()}
            onFocus={() => combo.openDropdown()}
            onBlur={() => combo.closeDropdown()}
            placeholder={placeholder}
            size="md"
            value={prefiled || term}
            onChange={(e) => {
              const { value } = e.target || '';
              setPrefiiled('');
              setTerm(value);
            }}
            bd="gray.2"
          />
        </Combobox.Target>
        <Combobox.Dropdown
          style={{
            border: '1px solid',
            borderColor: '#ccc',
            borderRadius: '6px',
            boxShadow: '0 10px 12px rgba(0,0,0,0.25)',
          }}
        >
          {!recents.length && !results.length && !loading && (
            <Text p={'md'} size="sm">
              {term.length < 3
                ? 'Provide at least 3 characters please.'
                : 'Nothing found'}
            </Text>
          )}
          {loading ? (
            <Loader />
          ) : (
            <Combobox.Options>
              <Combobox.Group label={term ? '' : 'Recent contacts'}>
                {(term ? results : recents).map((item) => (
                  <Option
                    key={item.id}
                    item={item}
                    term={term}
                    ItemRenderer={ItemRenderer}
                    onClick={() => {
                      combo.closeDropdown();
                      addRecent(item);
                      setRecents(getRecents(id));
                      setTerm('');
                      setPrefiiled(item.name);
                      onSelect(item);
                    }}
                  />
                ))}
              </Combobox.Group>
            </Combobox.Options>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </Paper>
  );
}
